import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as SearchIcon } from 'assets/svg/search.svg';
import { debounce, get } from 'lodash';
import { cache } from 'graph_ql/index';
import UserType from '../../../constants/user';
import { useQuery } from '@apollo/client';
import { AlgoliaInput, AlgoliaResponse } from 'graph_ql/types/types';
import { IAlgoliaUser } from 'graph_ql/users/types';
import { GET_USERS } from 'graph_ql/users/queries';
import { getUserProfilePhoto } from '../../../utils/getUserProfilePhoto';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Spinner } from 'react-bootstrap';
import { searchConsumerStyles } from './searchConsumerStyles';
import { Radio } from '@mui/material';
import { setSelectedStore } from '../../../redux/create-style/create-style.slice';
import { useAppDispatch } from '../../../redux/store';

const SearchConsumer = (props) => {
    const { selectedConsumer, setSelectedConsumer } = props;
    const [selectedOption, setSelectedOption] = useState<any>(selectedConsumer);
    const [searchTerm, setSearchTerm] = useState('');
    const [suggestions, setSuggestions] = useState<any[]>([]);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [isShowOptions, setIsShowOptions] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const getMoreTimeout = useRef<any>(null);
    const dispatch = useAppDispatch();

    const getUserVariables = useMemo(() => {
        cache.modify({
            fields: {
                algoliaGetUsers(existingTaskRefs, { readField }) {
                    return {};
                },
            },
        });
        return {
            query: searchTerm,
            filters: `userType:${UserType.Customer}`,
            page: 0,
            hit: 20,
        };
        // eslint-disable-next-line
    }, [searchTerm]);

    const { fetchMore: fetchUsers } = useQuery<AlgoliaResponse<IAlgoliaUser, 'algoliaGetUsers'>, AlgoliaInput>(
        GET_USERS,
        {
            variables: getUserVariables,
            fetchPolicy: 'network-only',
            onCompleted(res) {
                if (!res) return;
                onGetUsers(res, false);
            },
        },
    );

    const onGetUsers = (res, fetch) => {
        if (get(res, ['algoliaGetUsers', 'hits'], []).length) {
            const data = res.algoliaGetUsers.hits.map((item) => {
                return {
                    id: item.id,
                    fullName: `${item.firstName} ${item.lastName}`,
                    avatar: getUserProfilePhoto(item),
                };
            });
            if (res.algoliaGetUsers.page >= res.algoliaGetUsers.nbPages - 1) setHasMore(false);
            else {
                setHasMore(true);
                setCurrentPage(res?.algoliaGetUsers?.page);
            }
            if (fetch) setSuggestions((prev) => [...(prev || []), ...(data || [])]);
            else setSuggestions(data);
        } else {
            setHasMore(false);
        }
    };

    useEffect(() => {
        setCurrentPage(0);
        if (selectedConsumer) {
            setSearchTerm(selectedConsumer.fullName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchMoreData = async (page) => {
        const data = await fetchUsers({
            variables: {
                page,
                hit: 20,
            },
        });
        onGetUsers(data?.data, true);
    };

    useEffect(() => {
        if (suggestions.length === 0 && hasMore) {
            setCurrentPage((prev) => prev + 1);
            if (getMoreTimeout.current) {
                clearTimeout(getMoreTimeout.current);
            }
            getMoreTimeout.current = setTimeout(() => {
                fetchMoreData(currentPage + 1);
            }, 1500);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasMore, selectedOption]);

    const onSelectOption = (option) => {
        // Deselect current option
        if (selectedOption && option.id === selectedOption.id) {
            handleDeleteItem();
            return;
        }

        setSelectedOption(option);
        setSelectedConsumer(option);
        dispatch(setSelectedStore(null));
    };

    const handleDeleteItem = () => {
        setSelectedOption(null);
        setSelectedConsumer(null);
    };

    const classes = searchConsumerStyles({
        isShowOptions,
        suggestionsHeight: suggestions.length,
    });

    const debounceSearch = useRef(debounce((value) => {}, 300)).current;

    const onSearchCategory = (event) => {
        const value = event.target.value;
        setSearchTerm(value);
        debounceSearch(value);
    };

    return (
        <div className={classes.sortWrap}>
            <div className={classes.searchBox} onClick={() => setIsShowOptions((prev) => !prev)}>
                <SearchIcon />
                <div style={{ width: '100%' }}>
                    <input value={searchTerm} onChange={onSearchCategory} className={classes.input} />
                </div>
            </div>
            <div id={'scrollableDiv'} className={classes.selectedOptions}>
                <InfiniteScroll
                    dataLength={suggestions.length}
                    next={() => {
                        setCurrentPage((prev) => prev + 1);
                        if (getMoreTimeout.current) {
                            clearTimeout(getMoreTimeout.current);
                        }
                        getMoreTimeout.current = setTimeout(() => {
                            fetchMoreData(currentPage + 1);
                        }, 1500);
                    }}
                    hasMore={hasMore}
                    loader={
                        <div className={classes.noSuggestions}>
                            <Spinner animation="border" style={{ width: 20, height: 20 }} />
                        </div>
                    }
                    scrollableTarget="scrollableDiv"
                >
                    {suggestions.map((item: any) => (
                        <div
                            key={item?.id}
                            className={classes.optionItem}
                            onClick={() => {
                                onSelectOption(item);
                            }}
                        >
                            <img src={item.avatar} alt="" className={classes.suggestionAvatar} />
                            <div className={classes.selectedOptionTitle}>{item.fullName}</div>
                            <div>
                                <Radio
                                    color="default"
                                    name="address"
                                    value={Boolean(selectedOption && item.id === selectedOption.id)}
                                    checked={Boolean(selectedOption && item.id === selectedOption.id)}
                                    classes={{ root: classes.radio, checked: classes.checked }}
                                    onClick={() => {
                                        onSelectOption(item);
                                    }}
                                />
                            </div>
                        </div>
                    ))}
                </InfiniteScroll>
            </div>
        </div>
    );
};

export default SearchConsumer;
